/**
 * Mahjong's tiles as the service numbers them (chat lambda/mahjong.ts): 0…143, four of each of 34 kinds
 * (kind = tile >> 2), then the eight flowers 136…143. Kinds 0–8 are characters 萬 one to nine, 9–17 dots 筒,
 * 18–26 bamboo 條, 27–30 the winds 東南西北, 31–33 the dragons 中發白, and 34–41 the flowers 春夏秋冬 梅蘭竹菊.
 * The app only draws and names them; every rule is the service's.
 */

export const kindOf = (t: number): number => (t < 136 ? t >> 2 : 34 + (t - 136))

export type TileFace =
  | { suit: 'm' | 'p' | 's'; n: number }
  | { honor: 'E' | 'S' | 'W' | 'N' | 'C' | 'F' | 'P' }
  | { flower: number }

const HONORS = ['E', 'S', 'W', 'N', 'C', 'F', 'P'] as const
export const HAN_NUM = ['一', '二', '三', '四', '五', '六', '七', '八', '九'] as const
export const HONOR_HAN: Record<string, string> = { E: '東', S: '南', W: '西', N: '北', C: '中', F: '發', P: '白' }
export const FLOWER_HAN = ['春', '夏', '秋', '冬', '梅', '蘭', '竹', '菊'] as const
export const WIND_HAN: Record<string, string> = { E: '東', S: '南', W: '西', N: '北' }

export function faceOfKind(k: number): TileFace {
  if (k < 27) return { suit: (['m', 'p', 's'] as const)[Math.floor(k / 9)]!, n: (k % 9) + 1 }
  if (k < 34) return { honor: HONORS[k - 27]! }
  return { flower: k - 34 }
}

const SUIT_WORD = { m: 'characters', p: 'dots', s: 'bamboo' } as const
const HONOR_WORD: Record<string, string> = {
  E: 'East wind',
  S: 'South wind',
  W: 'West wind',
  N: 'North wind',
  C: 'Red dragon',
  F: 'Green dragon',
  P: 'White dragon',
}
const FLOWER_WORD = ['Spring', 'Summer', 'Autumn', 'Winter', 'Plum', 'Orchid', 'Bamboo flower', 'Chrysanthemum'] as const

/** A kind in words, for a reader of the screen: "5 of characters", "East wind", "Plum". */
export function kindName(k: number): string {
  const f = faceOfKind(k)
  if ('suit' in f) return `${f.n} of ${SUIT_WORD[f.suit]}`
  if ('honor' in f) return HONOR_WORD[f.honor]!
  return FLOWER_WORD[f.flower]!
}
/** A kind as the table writes it: 五萬, 三筒, 七條, 東, 中, 梅. */
export function kindHan(k: number): string {
  const f = faceOfKind(k)
  if ('suit' in f) return `${HAN_NUM[f.n - 1]}${{ m: '萬', p: '筒', s: '條' }[f.suit]}`
  if ('honor' in f) return HONOR_HAN[f.honor]!
  return FLOWER_HAN[f.flower]!
}
